import { useQueryClient } from '@tanstack/react-query';
import type { MaintenanceState, PublicConfig } from '@reflex/shared';
import { useConfig } from './useConfig';
import { useSocketMessage } from '@/hooks/socket/useSocket';
import { queryKeys } from '@/lib/query-keys';

/**
 * Current platform maintenance-mode state, read from the cached `GET /config`
 * payload. Returns a disabled default while the config query is in flight.
 */
export function useMaintenance(): MaintenanceState {
  const { data } = useConfig();
  return data?.maintenance ?? { enabled: false, message: '' };
}

/**
 * Keep the cached maintenance state fresh for already-connected clients.
 * Mounted once in the app shell: when an admin toggles maintenance mode the
 * server pushes a `maintenance` WebSocket message, and we patch the config
 * cache so every open session updates its banner without a reload. Fresh page
 * loads read the same state directly from `GET /config`.
 */
export function useMaintenanceSync(): void {
  const queryClient = useQueryClient();
  useSocketMessage((msg) => {
    if (msg.type !== 'maintenance') return;
    queryClient.setQueryData<PublicConfig>(queryKeys.config, (prev) =>
      prev ? { ...prev, maintenance: msg.maintenance } : prev,
    );
  });
}
